import {
  type DistanceUnit as CoreDistanceUnit,
  convert,
  type FormatBundle,
  getFormatSnapshot,
  labelFor,
  type ResolvedUnits,
} from '@brightdrop/localization-core';

import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import type { BatteryRange } from '~/common/models/asset.model';
import type { EstimatedRangedInKM } from '~/common/models/asset-report.model';
import { BATTERY_RANGE_VALUE_MAPPERS } from '~/common/utils/battery.utils';

/**
 * The root {@link LocalizationProvider} bundle, including pass-through when the core flag is off.
 * Returns undefined when no provider has mounted (e.g. isolated unit tests).
 */
export const readMountedFormatBundle = (): FormatBundle | undefined => {
  try {
    return getFormatSnapshot();
  } catch {
    return undefined;
  }
};

/**
 * Diagnostic formatters from `@brightdrop/localization-core`.
 *
 * `distance` is always required (see {@link useDiagnosticValueFormat} or
 * `useFormat().distance.format`). Other fields are optional and may be absent
 * when localization is off.
 */
export type DiagnosticValueFormat = {
  distance: (value: number, valueUnit: CoreDistanceUnit) => string;
  percent?: (roundedPercent: number) => string;
  recency?: (iso: string) => string | undefined;
  dateTime?: (iso: string) => string | undefined;
};

/**
 * A percent that has already been rounded the way this cell always rounded it.
 * `digits: 0` on the unrounded figure would turn 99.6 into 100.
 * @param rounded - The percent after the cell's own floor, ceil, trunc, or round.
 * @param format - Present only while localization is on.
 */
export const percentLabel = (
  rounded: number,
  format?: Pick<DiagnosticValueFormat, 'percent'>
): string => (format?.percent ? format.percent(rounded) : `${rounded}%`);

/**
 * The distance in parentheses beside a charge reading.
 * @param value - Distance in `sourceUnit`, as mapped from the API.
 * @param sourceUnit - Core unit code on the report estimated range.
 * @param format - Core distance formatter from {@link useDiagnosticValueFormat}.
 */
export const parentheticalDistance = (
  value: number,
  sourceUnit: CoreDistanceUnit,
  format: DiagnosticValueFormat
): string => format.distance(value, sourceUnit);

type ChargeReading = {
  value?: number;
  range?: BatteryRange;
  estimatedRange?: EstimatedRangedInKM;
};

/**
 * Charge as `85%` or `85% (241 mi)`.
 * The percent uses the battery range mapper when the payload names a band,
 * and truncation otherwise. The distance is converted once, at the end.
 * @param battery - State of charge and, when present, estimated range.
 * @param format - Core formatters; distance is required when range is shown.
 */
export const formatChargeReading = (
  battery: ChargeReading | undefined,
  format: DiagnosticValueFormat
): string => {
  if (typeof battery?.value !== 'number') {
    return DEFAULT_EMPTY_VALUE;
  }

  const estimated = battery.estimatedRange;
  const distance =
    typeof estimated?.value === 'number' && estimated.unitOfMeasure
      ? parentheticalDistance(estimated.value, estimated.unitOfMeasure, format)
      : undefined;

  const percent = percentLabel(
    battery.range !== undefined
      ? BATTERY_RANGE_VALUE_MAPPERS[battery.range](battery.value)
      : Math.trunc(battery.value),
    format
  );

  return distance ? `${percent} (${distance})` : percent;
};

/**
 * A distance on its own, not inside a percent.
 * @param value - Distance in `sourceUnit`.
 * @param sourceUnit - Core unit code on the report estimated range.
 * @param format - Core distance formatter from {@link useDiagnosticValueFormat}.
 */
export const distanceLabel = (
  value: number,
  sourceUnit: CoreDistanceUnit,
  format: DiagnosticValueFormat
): string => format.distance(value, sourceUnit);

/**
 * Asset report tire columns (API PSI) for the reader's unit and locale.
 */
export const formatReportTirePressure = (
  value: number,
  units: ResolvedUnits,
  locale: string
): string => {
  const format = readMountedFormatBundle();
  const converted = format
    ? format.pressure.raw(value, { valueUnit: 'PSI' })
    : convert('pressure', value, 'PSI', units.pressure);
  const label = format
    ? format.pressure.label()
    : labelFor(locale, 'pressure', units.pressure);
  return `${Math.round(converted)} ${label}`;
};
